import React, { useCallback } from 'react';
import { css } from '@emotion/css';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Button, Stack } from '@lyearn/ui';
import { QuizDetailsActions } from '@/modules/corporate/modules/quiz/views/QuizDetails/components/QuizDetailsAction/types';
import { getQuizDetailsActions } from '@/modules/corporate/modules/quiz/views/QuizDetails/components/QuizDetailsAction/utils';
import { useQuizDetailsContext } from '@/modules/corporate/modules/quiz/views/QuizDetails/context/QuizDetailsContext';
import getModifiedAttempts from '@/modules/corporate/modules/quiz/views/QuizDetails/utils/getQuizModifiedAttempts';

import { QuizPreviewType } from '../../types';

const borderRadiusStyles = css`
  border-radius: 2.8rem;
  min-width: 0;
`;

function QuizPreviewActions(props: QuizPreviewType) {
  const { t } = useTranslation(['QuizPlayer']);
  const { quiz, quizPerformance } = props;
  const { onShowQuiz, onRetakeQuizAndToggle, onShowAttemptHistory } = useQuizDetailsContext();

  const attemptsHistory = getModifiedAttempts(quizPerformance);
  const actions = getQuizDetailsActions(quiz, attemptsHistory, quizPerformance);

  const onClick = useCallback(
    (id: QuizDetailsActions) => {
      switch (id) {
        case QuizDetailsActions.StartQuiz:
        case QuizDetailsActions.ResumeQuiz:
          onShowQuiz();
          break;
        case QuizDetailsActions.ReattemptQuiz:
          onRetakeQuizAndToggle();
          break;
        case QuizDetailsActions.ViewResults:
          onShowQuiz();
          break;
        case QuizDetailsActions.AttemptsHistory:
          onShowAttemptHistory!();
          break;
      }
    },
    [onRetakeQuizAndToggle, onShowQuiz, onShowAttemptHistory],
  );

  return (
    <Stack className="native:absolute native:mb-24 sticky bottom-0 w-full self-center xs:mt-auto xs:mb-36">
      {actions.map((action, index) => {
        const isLast = index === actions.length - 1;
        return (
          <Button
            key={action.id}
            appearance={isLast ? 'primary' : 'secondary'}
            className={cn('h-56 flex-1 px-0', { 'me-12': !isLast }, borderRadiusStyles)}
            size="large"
            variant={isLast ? 'filled' : 'outlined'}
            onClick={() => onClick(action.id)}>
            {t(action.labelKey)}
          </Button>
        );
      })}
    </Stack>
  );
}

export default React.memo(QuizPreviewActions);
